import React, { useState, useEffect, useRef } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import '../../css/TeamBuildingChallenge/TeamBuildingChallenge.css';
import useTeamBuildingChallenge from '../../hooks/useTeamBuildingChallenge';
import Card from '../Card';
import Inventory from '../Inventory';

import { useQuickSell } from '../../hooks/useQuickSell';

const TeamBuildingChallenge = ({ fullSize = false }) => {
    const [selectedChallenge, setSelectedChallenge] = useState(0);
    const [selectedPackStatus, setSelectedPackStatus] = useState(null);
    const { challenges, completedChallenges, loading, error, completeChallenge, fetchSpecialPackStatus, openSpecialPack } = useTeamBuildingChallenge();
    const [timeLeft, setTimeLeft] = useState([]);
    const [team, setTeam] = useState([]);
    const [submitError, setSubmitError] = useState(null);
    const [submitSuccess, setSubmitSuccess] = useState(null);
    const [requirementStatus, setRequirementStatus] = useState([]);
    const [openedCardsID, setOpenedCardsID] = useState([]);
    const cardRefs = useRef([]);
    const [isOpening, setIsOpening] = useState(true);
    const [isGodPack, setIsGodPack] = useState(false); // State to track if the pack is a God Pack
    const [allCardsOpened, setAllCardsOpened] = useState(false); // State to track if all cards are opened
    const [lockOpening, setLockOpening] = useState(false);
    const [showBackStates, setShowBackStates] = useState([]); // Track which cards show back
    const [isOpeningStates, setIsOpeningStates] = useState([]); // Track which cards are opening (animation)
    const [cardsVisible, setCardsVisible] = useState(false); // Track when cards should be visible
    const timers = useRef([]);
    const inventoryRef = useRef(null);
    // Quicksell state
    const [selectedCardsForSell, setSelectedCardsForSell] = useState([]);
    const [totalQuickSellValue, setTotalQuickSellValue] = useState(0);
    const [isQuickSelling, setIsQuickSelling] = useState(false);
    const [showQuickSellConfirmation, setShowQuickSellConfirmation] = useState(false);
    const { groupQuickSell, fetchCardFantasyTeamStatus, isGroupQuickSelling } = useQuickSell();
    // Update total quick sell value when selectedCardsForSell changes
    useEffect(() => {
        if (selectedCardsForSell.length > 0) {
            // You may want to adjust this calculation to match your backend logic
            const total = selectedCardsForSell.reduce((sum, card) => {
                // Example: sum by tier * firepower
                return sum + ((card.tier || (card.card && card.card.tier) || 1) * ((card.stats && card.stats[0]?.firepower) || (card.card && card.card.player && card.card.player.stats && card.card.player.stats[0]?.firepower) || 0));
            }, 0);
            setTotalQuickSellValue(total);
        } else {
            setTotalQuickSellValue(0);
        }
    }, [selectedCardsForSell]);

    // Handle card selection for quick sell
    const handleCardClick = async (card) => {
        if (!allCardsOpened) return;
        setShowQuickSellConfirmation(false);
        if (selectedCardsForSell.some(selectedCard => selectedCard._id === card._id)) {
            setSelectedCardsForSell(selectedCardsForSell.filter(selectedCard => selectedCard._id !== card._id));
        } else {
            const status = await fetchCardFantasyTeamStatus(card._id);
            if (status.status === 'ACTIVE') {
                return;
            }
            setSelectedCardsForSell([...selectedCardsForSell, card]);
        }
    };

    // Quick sell selected cards
    const quickSellSelectedCards = async () => {
        if (isQuickSelling || isGroupQuickSelling) return;
        setIsQuickSelling(true);
        try {
            const result = await groupQuickSell(selectedCardsForSell.map(card => card._id));
            if (result) {
                setSelectedCardsForSell([]);
                setOpenedCardsID([]); // Close popup after selling
                setSelectedPackStatus(true); // Show Redeemed after quicksell
            }
        } catch (error) {
        } finally {
            setIsQuickSelling(false);
        }
    };

    // Quick sell all cards
    const quickSellAllCards = async () => {
        if (!showQuickSellConfirmation) {
            setShowQuickSellConfirmation(true);
            return;
        }
        if (isQuickSelling || isGroupQuickSelling) return;
        setIsQuickSelling(true);
        try {
            const sellableCards = [];
            for (const card of openedCardsID) {
                const status = await fetchCardFantasyTeamStatus(card._id);
                if (status.status !== 'ACTIVE') {
                    sellableCards.push(card);
                }
            }
            if (sellableCards.length === 0) {
                setIsQuickSelling(false);
                return;
            }
            const result = await groupQuickSell(sellableCards.map(card => card._id));
            if (result) {
                setOpenedCardsID([]);
                setSelectedPackStatus(true); // Show Redeemed after quicksell
            }
        } catch (error) {
        } finally {
            setIsQuickSelling(false);
            setShowQuickSellConfirmation(false);
        }
    };

    const containerStyle = {
        justifyContent: challenges.length < 4 ? 'center' : 'flex-start'
    };

    const requirementToText = (requirement, format = 'long') => {
        const cardText = requirement.amount === 1 ? 'card' : 'cards';
        const minmaxText = requirement.minmax === 'min' ? 'above' : 'below';
        const minmaxTextShort = requirement.minmax === 'min' ? '>' : '<';
        const rarityMap = {
            1: 'Bronze',
            2: 'Silver',
            3: 'Gold',
            4: 'Legendary'
        };

        const capitalizeFirstLetter = (string) => {
            return string.charAt(0).toUpperCase() + string.slice(1);
        };

        if (format === 'short') {
            switch (requirement.category) {
                case 'teamAvgStats':
                    return `Avg ${capitalizeFirstLetter(requirement.stat)} stat (${minmaxTextShort}${requirement.value})`;
                case 'stats':
                    return `${capitalizeFirstLetter(requirement.stat)} stat (${minmaxTextShort}${requirement.value})`;
                case 'rarity':
                    return `${rarityMap[requirement.value]} ${cardText}`;
                case 'team':
                    return `Unique teams`;
                case 'boosts':
                    return `${requirement.boost.name} boost (${minmaxTextShort}${requirement.value})`;
                default:
                    return '';
            }
        } else {
            switch (requirement.category) {
                case 'teamAvgStats':
                    return `Average ${capitalizeFirstLetter(requirement.stat)} stat of team ${minmaxText} ${requirement.value}`;
                case 'stats':
                    return `Minimum ${requirement.amount} ${cardText} with a ${capitalizeFirstLetter(requirement.stat)} stat of ${minmaxText} ${requirement.value}`;
                case 'rarity':
                    return `Minimum ${requirement.amount} ${rarityMap[requirement.value]} ${cardText}`;
                case 'team':
                    return `Amount of unique teams: ${requirement.value}`;
                case 'boosts':
                    return `Minimum ${requirement.amount} ${cardText} with a ${requirement.boost.name} boost ${minmaxText} ${requirement.value}`;
                default:
                    return '';
            }
        }
    };

    const requirementStatusText = (requirement, averageStats) => {
        switch (requirement.category) {
            case 'teamAvgStats':
                let avgStatName = requirement.stat;
                if (requirement.stat === 'awping') { avgStatName = 'sniping'; }
                if (requirement.stat === 'rating') { avgStatName = 'firepower'; }
                return `${averageStats[avgStatName] ? averageStats[avgStatName].toFixed(2) : '0'}/${requirement.value}`;
            case 'stats':
                let statName = requirement.stat;
                if (requirement.stat === 'awping') { statName = 'sniping'; }
                if (requirement.stat === 'rating') { statName = 'firepower'; }
                return requirement.minmax === 'min'
                    ? `${team.filter(card => card.card.player.stats[0][statName] > requirement.value).length}/${requirement.amount}`
                    : `${team.filter(card => card.card.player.stats[0][statName] < requirement.value).length}/${requirement.amount}`;
            case 'rarity':
                return `${team.filter(card => card.card.tier === requirement.value).length}/${requirement.amount}`;
            case 'team':
                const uniqueTeams = [...new Set(team.map(card => card.card.player.team._id))];
                return `${uniqueTeams.length}/${requirement.value}`;
            case 'boosts':
                return requirement.minmax === 'min'
                    ? `${team.filter(card => card.card.boosts.some(boost => boost.boost === requirement.boost._id && boost.value > requirement.value)).length}/${requirement.amount}`
                    : `${team.filter(card => card.card.boosts.some(boost => boost.boost === requirement.boost._id && boost.value < requirement.value)).length}/${requirement.amount}`;
            default:
                return '';
        }
    };

    const renderStars = (difficulty) => {
        const stars = [];
        for (let i = 0; i < 5; i++) {
            stars.push(
                <span key={i} className={i < difficulty ? 'star filled' : 'star'}>
                    &#9733;
                </span>
            );
        }
        return stars;
    };

    const addCard = (card) => {
        setTeam(prevTeam => {
            if (prevTeam.length >= 5) { return prevTeam; }
            if (prevTeam.find(c => c.card._id === card._id)) { return prevTeam; }
            if (prevTeam.find(c => c.player && c.player._id === card.player._id)) { return prevTeam; }

            // Find the first available position between 1 and 5
            let position = [1, 2, 3, 4, 5].find(p => !prevTeam.find(c => c.position === p));

            // Add the card to the team with the assigned position
            return [...prevTeam, { card: card, player: card.player._id, points: 0, position: position }];
        });
    }

    const removeCard = (card) => {
        setTeam(prevTeam => {
            const updatedTeam = prevTeam.filter(c => c.card._id !== card.card._id);
            return updatedTeam;
        });
    };

    const handleSubmitTeam = async (team) => {
        try {
            await completeChallenge(challenges[selectedChallenge]._id, team);
            setSubmitError(null);
            setTeam([]);

            // Refetch inventory when a challenge is completed
            if (inventoryRef.current) {
                inventoryRef.current.refetchInventory();
            }
        } catch (error) {
            setSubmitSuccess(null);
            setSubmitError(error.message);
        }
    }

    const isChallengeCompleted = (challenge) => {
        return Array.isArray(completedChallenges) && completedChallenges.some(completed => completed.challengeId === challenge._id);
    };

    const handleOpenPack = async (packID) => {
        if (!lockOpening) {
            setLockOpening(true); // Lock the function to prevent multiple executions
            try {
                // Reset these states before opening new pack
                setIsOpening(false); // Start with cards in closed state
                setAllCardsOpened(false);
                setCardsVisible(false); // Hide cards initially
                cardRefs.current = []; // Clear card refs
                clearTimers(); // Clear any existing timers

                const data = await openSpecialPack(packID);
                setOpenedCardsID(data.cards);

                // Check if this is a God Pack (you might need to adjust this logic based on your backend)
                const isGodPackResult = data.isGodPack || false;
                setIsGodPack(isGodPackResult);

                // Initialize showBack states - all cards start showing their backs
                setShowBackStates(Array(data.cards.length).fill(true));
                setIsOpeningStates(Array(data.cards.length).fill(false));
            } catch (error) {
                console.error('Error opening pack');
                setLockOpening(false);
            }
        }
    };

    const quickOpen = () => {
        setLockOpening(false);
        setIsOpening(false);
        // Set all cards to show front (not back) for quick open
        setShowBackStates(Array(openedCardsID.length).fill(false));
        setIsOpeningStates(Array(openedCardsID.length).fill(false));
        setCardsVisible(true); // Make sure cards are visible for quick open
        clearTimers();
        setAllCardsOpened(true); // Set the state to true when quick open is used
    };

    const clearTimers = () => {
        timers.current.forEach(timer => clearTimeout(timer));
        timers.current = [];
    };


    const justifyContent = () => {
        const packContainer = document.querySelector('.OpenPack-content');

        const checkCardContainer = () => {
            const cardContainer = document.getElementById('card');
            if (packContainer && cardContainer) {
                const totalCardsWidth = openedCardsID.length * cardContainer.clientWidth;
                const isOverflowing = totalCardsWidth > packContainer.clientWidth;

                if (isOverflowing) {
                    packContainer.style.justifyContent = 'flex-start';
                } else {
                    packContainer.style.justifyContent = 'center';
                }

                // Clear the interval once the cardContainer is found and processed
                clearInterval(intervalId);
            }
        };

        // Retry every second until the cardContainer is found
        const intervalId = setInterval(checkCardContainer, 1000);
    };

    // Helper function to wait for DOM element to be ready
    const waitForElement = (selector, maxWait = 2000) => {
        return new Promise((resolve) => {
            const checkElement = () => {
                const element = document.querySelector(selector);
                if (element) {
                    resolve(element);
                } else {
                    const timeout = setTimeout(checkElement, 50);
                    setTimeout(() => {
                        clearTimeout(timeout);
                        resolve(null);
                    }, maxWait);
                }
            };
            checkElement();
        });
    };

    useEffect(() => {
        clearTimers();

        if (openedCardsID.length > 0 && openedCardsID[0]) {
            setIsOpening(true); // Set isOpening to true when starting the sequence
            const baseDelay = isGodPack ? 600 : 400;
            const cardInterval = isGodPack ? 7200 : 3200; // Double interval for God Packs
            const flipDelay = 150;

            // Wait a short time for all cards to render/mount, then wait for all elements and start opening sequence
            const startTimer = setTimeout(async () => {
                // Wait for all card elements to be ready before making them visible
                const cardElementPromises = openedCardsID.map((cardData) =>
                    waitForElement(`.card-${cardData._id}`)
                );

                // Wait for all card elements to be ready
                await Promise.all(cardElementPromises);

                // Now make all cards visible since they're all ready
                setCardsVisible(true);

                // Open cards one by one with intervals
                openedCardsID.forEach((cardData, index) => {
                    const cardOpenTimer = setTimeout(async () => {
                        const cardId = cardData._id;

                        // Wait for the DOM element to be ready
                        const cardElement = await waitForElement(`.card-${cardId}`);

                        // Set card as opening and flip to show front simultaneously
                        setIsOpeningStates(prev => {
                            const updated = [...prev];
                            updated[index] = true;
                            return updated;
                        });

                        // After animation delay, call openCard
                        const flipTimer = setTimeout(() => {
                            // Function to attempt opening card with retry
                            const attemptOpenCard = (cardIdx, attempt = 1, maxAttempts = 3) => {
                                if (cardRefs.current[cardIdx] && typeof cardRefs.current[cardIdx].openCard === 'function' && cardElement) {
                                    setShowBackStates(prev => {
                                        const updated = [...prev];
                                        updated[index] = false;
                                        return updated;
                                    });
                                    setTimeout(() => {
                                        cardRefs.current[cardIdx].openCard();
                                    }, flipDelay * 2);

                                    // Set isOpening to false after the card opening animation completes
                                    setTimeout(() => {
                                        setIsOpeningStates(prev => {
                                            const updated = [...prev];
                                            updated[cardIdx] = false;
                                            return updated;
                                        });
                                    }, cardInterval);

                                } else if (attempt < maxAttempts) {
                                    const retryTimer = setTimeout(() => {
                                        attemptOpenCard(cardIdx, attempt + 1, maxAttempts);
                                    }, 50 * attempt);
                                    timers.current.push(retryTimer);
                                } else {
                                    // Fallback: try to directly add the active class
                                    const fallbackCardElement = document.querySelector(`.card-${cardId}`) || cardElement;
                                    if (fallbackCardElement) {
                                        fallbackCardElement.classList.add('active');
                                        fallbackCardElement.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });

                                        // Set isOpening to false for fallback as well
                                        setTimeout(() => {
                                            setIsOpeningStates(prev => {
                                                const updated = [...prev];
                                                updated[cardIdx] = false;
                                                return updated;
                                            });
                                        }, cardInterval);
                                    }
                                }
                            };

                            attemptOpenCard(index);
                        }, flipDelay);
                        timers.current.push(flipTimer);

                        // Unlock opening after the last card is opened and fully animated
                        if (index === openedCardsID.length - 1) {
                            const finalTimer = setTimeout(() => {
                                setLockOpening(false);
                                setAllCardsOpened(true);
                            }, cardInterval + flipDelay);
                            timers.current.push(finalTimer);
                        }
                    }, baseDelay + (index * cardInterval));
                    timers.current.push(cardOpenTimer);
                });
            }, 300); // Short delay to ensure all cards are rendered
            timers.current.push(startTimer);
        }

        return () => {
            clearTimers();
        };
    }, [openedCardsID, isGodPack]);

    useEffect(() => {
        justifyContent();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [openedCardsID, allCardsOpened]);

    useEffect(() => {
        const calculateAverageStats = () => {
            if (team.length === 0) {
                return {};
            }

            const totalStats = team.reduce((acc, card) => {
                for (const [stat, value] of Object.entries(card.card.player.stats[0])) {
                    acc[stat] = (acc[stat] || 0) + value;
                }
                return acc;
            }, {});

            const avgStats = Object.keys(totalStats).reduce((acc, stat) => {
                acc[stat] = totalStats[stat] / team.length;
                return acc;
            }, {});

            return avgStats;
        };

        const isRequirementFulfilled = (requirement, averageStats) => {
            let status = false;
            switch (requirement.category) {
                case "rarity":
                    status = team.filter(card => card.card.tier === requirement.value).length >= requirement.amount;
                    break;
                case "team":
                    const uniqueTeams = [...new Set(team.map(card => card.card.player.team._id))];
                    status = uniqueTeams.length === requirement.value;
                    break;
                case "stats":
                    let statName = requirement.stat;
                    if (requirement.stat === 'awping') { statName = 'sniping'; }
                    if (requirement.stat === 'rating') { statName = 'firepower'; }
                    status = requirement.minmax === "min"
                        ? team.filter(card => card.card.player.stats[0][statName] > requirement.value).length >= requirement.amount
                        : team.filter(card => card.card.player.stats[0][statName] < requirement.value).length >= requirement.amount;
                    break;
                case "teamAvgStats":
                    let avgStatName = requirement.stat;
                    if (requirement.stat === 'awping') { avgStatName = 'sniping'; }
                    if (requirement.stat === 'rating') { avgStatName = 'firepower'; }
                    const avgStatValue = averageStats[avgStatName] || 0;
                    status = requirement.minmax === "min"
                        ? avgStatValue >= requirement.value
                        : avgStatValue <= requirement.value;
                    break;
                case "boosts":
                    status = requirement.minmax === "min"
                        ? team.filter(card => card.card.boosts.some(boost => boost.boost === requirement.boost._id && boost.value > requirement.value)).length >= requirement.amount
                        : team.filter(card => card.card.boosts.some(boost => boost.boost === requirement.boost._id && boost.value < requirement.value)).length >= requirement.amount;
                    break;
                default:
                    status = false;
            }
            return {
                status,
                text: requirementStatusText(requirement, averageStats)
            };
        };

        if (challenges[selectedChallenge]) {
            const avgStats = calculateAverageStats();
            const status = challenges[selectedChallenge].requirements.map(requirement => isRequirementFulfilled(requirement, avgStats));
            setRequirementStatus(status);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [team, challenges, selectedChallenge]);

    useEffect(() => {
        if (challenges.length <= 3) { return; }
        const challengeContent = document.querySelector('.tbc-challenges-container');
        if (challengeContent) {
            const handleScroll = (event) => {
                if (event.deltaY !== 0) {
                    challengeContent.scrollLeft += event.deltaY;
                    event.preventDefault();
                }
            };
            challengeContent.addEventListener('wheel', handleScroll);
            return () => {
                challengeContent.removeEventListener('wheel', handleScroll);
            };
        }
    }, [challenges]);

    useEffect(() => {
        setSubmitError(null);
        setSubmitSuccess(null);
    }, [team, selectedChallenge]);

    useEffect(() => {
        const updateCountdown = () => {
            const newTimeLeft = challenges?.map(challenge => {
                const endDate = new Date(challenge.endDate).getTime();
                const now = new Date().getTime();
                const distance = endDate - now;

                if (distance < 0) {
                    return 'Expired';
                }

                const hours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
                const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
                const seconds = Math.floor((distance % (1000 * 60)) / 1000);

                return `${hours}h ${minutes}m ${seconds}s`;
            });

            setTimeLeft(newTimeLeft);
        };

        updateCountdown();
        const interval = setInterval(updateCountdown, 1000);

        return () => clearInterval(interval);
    }, [challenges]);

    useEffect(() => {
        const fetchPackStatus = async () => {
            if (selectedChallenge !== null && challenges[selectedChallenge]) {
                const packStatus = await fetchSpecialPackStatus(challenges[selectedChallenge]?.reward._id);
                setSelectedPackStatus(packStatus.hasOpened);
            }
        };
        fetchPackStatus();
        setLockOpening(false);
        setOpenedCardsID([]);
        setAllCardsOpened(false);
        setIsGodPack(false);
        setShowBackStates([]);
        setIsOpeningStates([]);
        setSelectedCardsForSell([]);
        setCardsVisible(false);
        clearTimers();
        // Reset card refs to ensure clean state for next pack opening
        cardRefs.current = [];

        // Refetch inventory when selectedChallenge changes
        if (inventoryRef.current && challenges.length > 0) {
            inventoryRef.current.refetchInventory();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedChallenge, challenges]);

    return (
        <div className={`main-container col-10 offset-1`}>
            <div className='scroll-container'>

                <div className='title-container' style={{ marginBottom: '0.5vw', position: 'relative' }}>
                    <div className='col-10 inventory-box'>
                        <div className='col-auto'>
                            <h1 className='title'>Team Building Challenge</h1>
                        </div>
                    </div>
                </div>

                <div className='tbc-challenges-main-container'>
                    <h1 className='title-inventory'>Challenges</h1>
                    <div className='tbc-challenges-container' style={containerStyle}>
                        {loading && <div className='loader'></div>}
                        {error && <h1 className='error'>{error}</h1>}
                        {!loading && !error && challenges.length === 0 && (
                            <h1>No challenges available</h1>
                        )}
                        {!loading && !error && challenges.length > 0 && (
                            challenges.map((challenge, index) => (
                                <div key={index} className={`tbc-challenge-wrapper`} onClick={() => setSelectedChallenge(index)}>
                                    <div className={`tbc-challenge ${selectedChallenge === index ? 'selected' : ''} ${isChallengeCompleted(challenge) ? 'completed' : ''}`}>
                                        <h1 className='tbc-challenge-name'>{challenge.name} {isChallengeCompleted(challenge) && "✔"}</h1>
                                        <p className='tbc-challenge-timer'>{timeLeft[index]}</p>
                                        <p className='tbc-challenge-desc'><span>Difficulty:</span> {renderStars(challenge.difficulty)}</p>
                                        <p className='tbc-challenge-desc'><span>Reward:</span> {challenge.reward.name}</p>
                                        <p className='tbc-challenge-desc'><span>Requirements:</span></p>
                                        <ul className='tbc-challenge-desc'>
                                            {challenge.requirements.map((requirement, index) => (
                                                <li key={index}>{
                                                    requirementToText(requirement)
                                                }</li>
                                            ))}
                                        </ul>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </div>

                {selectedChallenge !== null && !isChallengeCompleted(challenges[selectedChallenge]) ? (
                    <div>
                        <div className='event-page-container'>
                            <h1 className='title-inventory'>Team</h1>
                        </div>

                        <div className='tbc-challenge-main-container'>
                            <div className='tbc-challenge-team-container'>
                                <div className='tbc-challenge-requirements-container'>
                                    <h1 className='title-inventory'>Requirements</h1>

                                    <div className='tbc-challenge-requirements'>
                                        {loading && <div className='loader' />}
                                        <table className='tbc-challenge-requirement'>
                                            <tbody>
                                                {challenges[selectedChallenge]?.requirements.map((requirement, index) => (
                                                    <tr key={index}>
                                                        <td>
                                                            <div className='requirements-status'>
                                                                <div className={`circle ${requirementStatus[index]?.status ? 'green' : 'red'}`}></div>
                                                            </div>
                                                        </td>
                                                        <td>{requirementToText(requirement, "short")}</td>
                                                        <td>
                                                            {requirementStatus[index] ? requirementStatus[index].text : ''}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>

                                    </div>
                                </div>
                                <div className='event-page-team-container' style={{ minHeight: '28.5vw' }}>
                                    <div className='event-fantasy-team-container'>
                                        {Array.from({ length: 5 }).map((_, index) => {
                                            const card = team.find(c => c.position === index + 1);
                                            return (
                                                <div key={index} className='event-fantasy-player'>
                                                    {card ? (
                                                        <div className='card-point-container' onClick={() => removeCard(card)}>
                                                            <Card card={card.card} />
                                                        </div>
                                                    ) : (
                                                        <img className='fantasy-player-image' src={'https://static.hltv.org/images/playerprofile/bodyshot/unknown.png'} alt='player' />
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                    <button className='default-button submit-team-button' onClick={() => handleSubmitTeam(team)}>Submit Team</button>
                                    {submitError && <p className='error' style={{ alignSelf: 'center' }}>{submitError}</p>}
                                    {submitSuccess && <p className='success' style={{ alignSelf: 'center' }}>{submitSuccess}</p>}
                                </div>
                            </div>

                            <div className='event-page-inventory-container' style={{ position: 'relative' }}>
                                <Inventory
                                    ref={inventoryRef}
                                    onCardClick={addCard}
                                    fullSize={true}
                                    team={team}
                                />
                            </div>
                        </div>
                    </div>
                ) : (
                    <div>
                        <div className='event-page-container'>
                            <h1 className='title-inventory'>Reward</h1>
                        </div>

                        <div className='OpenPack-container' style={{ width: '100%' }}>
                            <div className='OpenPack-content'>
                                {selectedChallenge !== null && !selectedPackStatus ? (
                                    openedCardsID.length === 0 ? (
                                        <div className='tbc-challenge-reward-opened'>
                                            <button className='default-button close-button' onClick={() => { handleOpenPack(challenges[selectedChallenge]?.reward._id) }}>Open</button>
                                        </div>
                                    ) : (
                                        <div className={`${isGodPack ? "god-pack-overlay" : "tbc-OpenPack-container"}`}>
                                            {isGodPack && <div className="god-pack-particles"></div>}
                                            <div className='tbc-OpenPack-content'>
                                                {openedCardsID.map((card, index) => (
                                                    <div
                                                        key={index}
                                                        className={`cardDisplay-container ${allCardsOpened && selectedCardsForSell.some(selectedCard => selectedCard._id === card._id) ? 'highlighted-card' : ''}`}
                                                        style={{
                                                            paddingBottom: 0,
                                                            marginBottom: 0,
                                                            cursor: allCardsOpened ? 'pointer' : 'default',
                                                            opacity: cardsVisible ? 1 : 0,
                                                            transition: 'opacity 0.3s ease-in-out'
                                                        }}
                                                        onClick={() => allCardsOpened && handleCardClick(card)}
                                                    >
                                                        <div className={`${allCardsOpened && 'card-hoverable'}`}>

                                                            <Card
                                                                ref={(el) => (cardRefs.current[index] = el)}
                                                                cardID={card._id}
                                                                size={"small"}
                                                                showBack={showBackStates[index]}
                                                                isOpening={isOpeningStates[index]}
                                                                isSlowOpening={isGodPack}
                                                                key={`${card._id}-${openedCardsID.length}`}
                                                            />
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                            <div className='OpenPack-footer' style={{ minHeight: '5vw' }}>
                                                {(!isGodPack || (isGodPack && allCardsOpened)) && !allCardsOpened && (
                                                    <button className='default-button close-button' onClick={quickOpen}>Quick Open</button>
                                                )}
                                                {!isGodPack && allCardsOpened && (
                                                    <div className='quick-sell-section' style={{ position: 'relative' }}>
                                                        {selectedCardsForSell.length > 0 && (
                                                            <div className='quick-sell-info'>
                                                                <span>Selected: {selectedCardsForSell.length} cards</span>
                                                                <span>Total value: {totalQuickSellValue}</span>
                                                            </div>
                                                        )}
                                                        <button
                                                            className={`${allCardsOpened ? 'red-button' : 'default-button'} close-button`}
                                                            onClick={selectedCardsForSell.length > 0 ? quickSellSelectedCards : quickSellAllCards}
                                                            disabled={isQuickSelling}
                                                        >
                                                            {isQuickSelling ? <div className="loader-small" /> : selectedCardsForSell.length > 0 ? `Quick Sell (${selectedCardsForSell.length})` : showQuickSellConfirmation ? 'You Sure?' : 'Quick Sell All'}
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    )

                                ) : (
                                    <div className='tbc-challenge-reward-opened'>
                                        <h1>Redeemed</h1>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}

            </div>
        </div>
    );
};

export default TeamBuildingChallenge;